/** HÂRTIA DE CALC a editorului: planul arhitecturii, desenat de vizualizator
 *  (`StoreyView`), PESTE geometrie și stins — ca imaginea pusă sub mână în
 *  editorul de tronson (`viewer/tronson/Calcul.tsx`). Aici nu e o poză, e
 *  documentul canonic pe care stă soluția: același desen ca în vizualizator,
 *  pe planșeul nivelului de pe ecran — schimbi nivelul din meniu, se schimbă
 *  și el.
 *
 *  Cele două desene nu stau în același cadru, și de aici tot ce e aici:
 *   * vizualizatorul desenează în METRI, în cadrul desenului
 *     (`canonical.northUp`: pe grilele obișnuite Y-ul documentului e întors,
 *     pe cele northing-first e un sfert de tură) și mutat la originea locală
 *     (`origineDesen`);
 *   * geometria structurii stă în MILIMETRI georeferențiați
 *     (`arhimport.canonic._mm`: aceeași regulă a nordului, fără întoarcerea
 *     Y-ului, plus originea documentului), rotiți cu −θ în cadrul clădirii
 *     (`editare._poligon`), iar ecranul editorului îi aduce înapoi la metri
 *     cu Y-ul întors (`editorGeometrie.laEcran`).
 *  `transformarea` compune drumul într-o singură matrice SVG, ca planul să
 *  cadă exact pe pereții pe care îi desenează deja editorul.
 */
import { northingFirst, type StoreyPlan } from "../viewer/canonical";
import { laEcran, type Cadru, type NivelContext, type Pt } from "./editorGeometrie";

/** Cât se vede planul la deschidere: aproape stins, ca să nu acopere
 *  geometria pe care o desenezi. */
export const OPACITATE_IMPLICITA = 0.3;
export type Calc = { vizibil: boolean; opacitate: number };
export const CALC_IMPLICIT: Calc = { vizibil: false, opacitate: OPACITATE_IMPLICITA };

/** `crs`-ul documentului: originea (metri) și grila. */
export type Crs = { origin?: readonly number[] | null; epsg?: number | null } | null | undefined;

/** Un punct al vizualizatorului (metri, după `northUp` și originea locală
 *  `origine`) → milimetri georeferențiați, cadrul geometriei dinainte de −θ. */
export function spreMilimetri(crs: Crs, origine: readonly [number, number],
                              p: readonly [number, number]): Pt {
  const ox = Number(crs?.origin?.[0] ?? 0);
  const oy = Number(crs?.origin?.[1] ?? 0);
  const u = p[0] + origine[0];
  const v = p[1] + origine[1];
  if (northingFirst(crs as { epsg?: number } | null)) {
    // sfertul de tură `(a, b) → (b, −a)` e același pe amândouă drumurile, și
    // pe origine (`canonical_model.read.north_up_xy`)
    return [(u + oy) * 1000, (v - ox) * 1000];
  }
  // desenul e documentul cu Y-ul întors; structura ține documentul neîntors
  return [(u + ox) * 1000, (oy - v) * 1000];
}

/** Matricea SVG (`matrix(a b c d e f)`) care duce desenul vizualizatorului pe
 *  ecranul editorului: drumul de mai sus, afin, scos din imaginea a trei
 *  puncte. */
export function transformarea(crs: Crs, origine: readonly [number, number],
                              thetaGrade: number, cadru: Cadru): string {
  const th = -(thetaGrade * Math.PI) / 180;         // ca `editare._poligon`
  const c = Math.cos(th);
  const s = Math.sin(th);
  const f = (p: readonly [number, number]): Pt => {
    const [x, y] = spreMilimetri(crs, origine, p);
    return laEcran(cadru, [x * c - y * s, x * s + y * c]);
  };
  const o = f([0, 0]);
  const ex = f([1, 0]);
  const ey = f([0, 1]);
  // fără rotunjire: coeficienții înmulțesc coordonate de ordinul miilor de
  // metri (originea locală), iar a șasea zecimală s-ar vedea pe ecran
  return `matrix(${ex[0] - o[0]} ${ex[1] - o[1]} ${ey[0] - o[0]} `
       + `${ey[1] - o[1]} ${o[0]} ${o[1]})`;
}

/** Planșeele de desenat pe nivelul editorului: cel cu id-ul planșeului
 *  (`storey`); fără el, cele de la aceeași cotă (`level`) ale clădirii
 *  corpului (`cod` «C2-T1» → «C2»), apoi oricare de la cota aia. */
export function planseeleNivelului(planuri: readonly StoreyPlan[],
                                   nivel: Pick<NivelContext, "storey" | "level">,
                                   cod: string | null): StoreyPlan[] {
  if (nivel.storey) {
    const exact = planuri.filter((p) => p.id === nivel.storey);
    if (exact.length) return exact;
  }
  if (nivel.level == null) return [];
  const laCota = planuri.filter((p) => p.level === nivel.level);
  const cladire = cod ? cod.split("-")[0] : null;
  const ale = cladire ? laCota.filter((p) => p.cod === cladire) : [];
  return ale.length ? ale : laCota;
}
